import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  Flex,
  Heading,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { NAV_HEIGHT } from 'constants/misc'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import Attribute from 'modules/Attributes/Attribute'

import { Card, InlineError } from 'components'

import { CleanResponseData, ResponseData } from 'api/types'
import { useCollectionDataSingleRaw } from 'api/useCollectionDataSingle'

import { EntityComponentPropsTypes } from 'interfaces/entity.interface'

import { relationGetDisplayValue } from 'utils/relational'
import resolvePath from 'utils/resolvePath'
import unCamelize from 'utils/unCamelize'
import { useAppParams } from 'routes/utils'
import { apps, appsListUnion } from 'config/apps'

const Breadcrumbs = ({
  app,
  page,
  subPage,
  modelName,
  entityName,
  entityId,
}: {
  app: appsListUnion
  page: string
  subPage: string
  modelName: string
  entityName: CleanResponseData | null
  entityId: string
}) => {
  const hasSubPage = subPage === entityId ? '' : subPage

  return (
    <Breadcrumb fontSize={'sm'} textAlign={'center'} color='gray.700'>
      <BreadcrumbItem
        _hover={{
          textDecoration: 'underline',
        }}
      >
        <Link style={{ textDecoration: 'none' }} to={`/${app}/`}>
          {apps[app]?.name ?? app}
        </Link>
      </BreadcrumbItem>
      <BreadcrumbItem _hover={{ textDecoration: 'underline' }}>
        <Link to={`/${app}/${page}${hasSubPage ? `/${subPage}` : ''}`}>
          {modelName}
        </Link>
      </BreadcrumbItem>
      <BreadcrumbItem isCurrentPage>
        <Text
          fontWeight={500}
          maxW='200px'
          textOverflow={'ellipsis'}
          overflow='hidden'
          whiteSpace='nowrap'
        >
          {entityName}
        </Text>
      </BreadcrumbItem>
    </Breadcrumb>
  )
}

export default ({
  app,
  page,
  model,
  entityId,
  viewData,
}: EntityComponentPropsTypes) => {
  const navigate = useNavigate()
  const { pageSlug, subPageSlug } = useAppParams()
  const [queryParams] = useSearchParams()
  const searchType = queryParams.get('searchType')
  const searchString = queryParams.get('searchString')

  const { isLoading, data, error } = useCollectionDataSingleRaw(
    app,
    model.endpoint,
    entityId,
    { airtableBase: viewData?.airtableBase }
  )
  return (
    <Box
      overflow='auto'
      bg='gray.100'
      flex={1}
      py={20}
      height={`calc(100vh - ${NAV_HEIGHT})`}
    >
      <Box maxW='3xl' width='100%' mx='auto'>
        <Flex mb={4} justifyContent='space-between'>
          <Box
            display='flex'
            fontSize='sm'
            fontWeight={500}
            color='gray.700'
            _hover={{
              textDecoration: 'underline',
            }}
            cursor='pointer'
            onClick={() => {
              const params = new URLSearchParams()
              if (searchType) params.set('searchType', searchType)
              if (searchString) params.set('searchString', searchString)
              navigate(params.toString() ? `/?${params.toString()}` : '/', {
                replace: true,
              })
            }}
          >
            ← Back to search
          </Box>
          <Breadcrumbs
            app={app}
            page={pageSlug}
            subPage={subPageSlug}
            entityId={entityId}
            entityName={
              data
                ? relationGetDisplayValue(
                    data[model.displayKey ?? ''] as ResponseData
                  )
                : ''
            }
            modelName={model.name}
          />
          <Link to={resolvePath([app, page.path])}>
            <Box
              display='flex'
              fontSize='sm'
              fontWeight={500}
              color='gray.700'
              _hover={{
                textDecoration: 'underline',
              }}
            >
              View All {model.name}
            </Box>
          </Link>
        </Flex>
        <Heading
          fontSize='2xl'
          fontWeight={600}
          lineHeight='1.6'
          mb={6}
          textAlign={'center'}
        >
          {data &&
            relationGetDisplayValue(
              data[model.displayKey ?? ''] as ResponseData
            )}
        </Heading>
      </Box>

      <Box maxW='3xl' width='100%' mx='auto'>
        <Card>
          <Box px={6} py={2}>
            {isLoading && <Spinner />}
            {error && <InlineError />}

            {data &&
              (page?.entity?.renderDetail
                ? page?.entity?.renderDetail(data)
                : model.schema.columns.map((fieldModel) => {
                    const { key } = fieldModel

                    return (
                      <Attribute
                        key={unCamelize(key)}
                        rowId={data.id}
                        collection={model.endpoint}
                        fieldModel={fieldModel}
                        value={data[key]}
                      />
                    )
                  }))}
          </Box>
        </Card>
      </Box>
    </Box>
  )
}
